/**
 * Touch paths (docs/design/visual-overhaul.md §12):
 *
 * - Long-press is the touch screen's right-click. A finger held still for
 *   LONG_PRESS_MS on anything sends that element a `contextmenu` event at
 *   the finger (so every `onContextMenu` in the app works by touch, iOS
 *   included, which sends none); moving more than LONG_PRESS_SLOP px first
 *   cancels it. The click that the lifted finger would make is swallowed,
 *   and so is the browser's own contextmenu of that same press (Android).
 * - The edge swipe: a finger that starts within EDGE_PX of the left edge and
 *   travels right by EDGE_MIN_TRAVEL (mostly sideways) calls `onSwipe`.
 *   A gesture the browser takes for itself (iOS's own swipe-back) ends in
 *   pointercancel and does nothing here.
 */

export const LONG_PRESS_MS = 450;
export const LONG_PRESS_SLOP = 10;
export const EDGE_PX = 24;
export const EDGE_MIN_TRAVEL = 64;

let installed = false;

/** Installs the long-press handling once for the page; returns nothing to undo (page life). */
export function installLongPress(): void {
  if (installed || typeof document === 'undefined') return;
  installed = true;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let start: { x: number; y: number; id: number; target: Element } | null = null;
  /** A long-press fired in this press: eat its click and the native contextmenu. */
  let fired = false;
  const cancel = () => {
    clearTimeout(timer);
    timer = undefined;
    start = null;
  };
  document.addEventListener(
    'pointerdown',
    (e) => {
      cancel();
      fired = false;
      if (e.pointerType !== 'touch' || !e.isPrimary) return;
      const target = e.target instanceof Element ? e.target : null;
      // fields keep the system's own press (select, paste)
      if (
        !target ||
        target.closest('input, textarea, [contenteditable="true"], [data-no-longpress]')
      )
        return;
      start = { x: e.clientX, y: e.clientY, id: e.pointerId, target };
      timer = setTimeout(() => {
        const s = start;
        cancel();
        if (!s || !s.target.isConnected) return;
        fired = true;
        navigator.vibrate?.(8);
        s.target.dispatchEvent(
          new MouseEvent('contextmenu', {
            bubbles: true,
            cancelable: true,
            composed: true,
            clientX: s.x,
            clientY: s.y,
            screenX: s.x,
            screenY: s.y,
            button: 2,
            buttons: 0,
          }),
        );
      }, LONG_PRESS_MS);
    },
    true,
  );
  document.addEventListener(
    'pointermove',
    (e) => {
      if (!start || e.pointerId !== start.id) return;
      if (Math.hypot(e.clientX - start.x, e.clientY - start.y) > LONG_PRESS_SLOP) cancel();
    },
    true,
  );
  for (const type of ['pointerup', 'pointercancel'] as const)
    document.addEventListener(type, (e) => start?.id === e.pointerId && cancel(), true);
  // the press that opened a menu must not also tap what is under it
  document.addEventListener(
    'click',
    (e) => {
      if (!fired) return;
      fired = false;
      e.preventDefault();
      e.stopPropagation();
    },
    true,
  );
  // Android sends its own contextmenu for the same press: ours already went
  document.addEventListener(
    'contextmenu',
    (e) => {
      if (e.isTrusted && fired) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    true,
  );
}

/**
 * Calls `onSwipe` for a swipe from the left edge (touch or pen). Returns the
 * stop. `enabled` is asked at the start of each gesture. `zone`: a selector
 * whose element also starts one (the app's edge strip, which covers the
 * safe area of a phone on its side, wider than EDGE_PX).
 */
export function installEdgeSwipe(
  onSwipe: () => void,
  enabled: () => boolean,
  zone?: string,
): () => void {
  if (typeof document === 'undefined') return () => {};
  let g: { id: number; x: number; y: number } | null = null;
  const down = (e: PointerEvent) => {
    g = null;
    if (e.pointerType === 'mouse' || !e.isPrimary) return;
    const inZone = !!zone && e.target instanceof Element && !!e.target.closest(zone);
    if ((e.clientX > EDGE_PX && !inZone) || !enabled()) return;
    g = { id: e.pointerId, x: e.clientX, y: e.clientY };
  };
  const up = (e: PointerEvent) => {
    const s = g;
    g = null;
    if (!s || e.pointerId !== s.id) return;
    const dx = e.clientX - s.x;
    const dy = Math.abs(e.clientY - s.y);
    if (dx >= EDGE_MIN_TRAVEL && dy < dx / 2) onSwipe();
  };
  const cancel = (e: PointerEvent) => {
    if (g && e.pointerId === g.id) g = null;
  };
  document.addEventListener('pointerdown', down, true);
  document.addEventListener('pointerup', up, true);
  document.addEventListener('pointercancel', cancel, true);
  return () => {
    document.removeEventListener('pointerdown', down, true);
    document.removeEventListener('pointerup', up, true);
    document.removeEventListener('pointercancel', cancel, true);
  };
}
